<template lang="html">
  <div class="draft-admin">
    <nav :class="{ open }">
      <header>
        <router-link class="back" :to="`/draft/${draft.id}/settings`" title="Back to settings">
          <i class="fas fa-arrow-left"></i>
        </router-link>

        <div class="search-container" v-if="draft.status === 'running'">
          <input
            type="search"
            placeholder="Search players"
            v-model="search"
          >
        </div>

        <div class="close-container" @click="close">
          <i class="fas fa-times" />
        </div>
      </header>

      <div class="start-container" v-if="draft.status !== 'running'">
        <img src="../assets/images/logo.png" width="150"><br><br>

        <button class="primary" @click="start" :disabled="starting">
          {{ startButtonText }}
        </button>
      </div>

      <div class="available" v-if="showAvailable">
        <position-filter :position.sync="positionFilter" />

        <div class="players-container">
          <div
            v-for="player in availablePlayers"
            :class="['player', { selected: player === selectedPlayer }]"
            :key="player.id"
          >
            <div class="player-info" @click="selectPlayer(player)">
              <div class="number">{{ player.rank }}</div>
              <div class="name">{{ player.name }}</div>
              <div class="team">{{ player.team }}</div>
              <div class="position">{{ player.position }}</div>
            </div>

            <div class="player-actions" v-show="player === selectedPlayer">
              <button @click="draftPlayer(player)">
                Draft
              </button>

              <button class="hollow" @click="cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="actions">
        <div class="main-actions">
          <div class="button-group" v-if="draft.status === 'running'">
            <button @click="pause">
              Pause
            </button>

            <button class="hollow" v-if="isCommissioner" @click="resetDraft">
              Reset
            </button>
          </div>

          <div @click="undo" v-if="showUndo" title="Undo last pick">
            <i class="fas fa-undo" />
          </div>

          <div @click="fullScreen" v-if="draft.status === 'running'" title="Enter fullscreen mode">
            <i class="fas fa-expand-arrows-alt" />
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  data() {
    return {
      selectedPlayer: null,
      search: '',
      positionFilter: 'ALL',
      starting: false,
    };
  },

  computed: {
    ...mapState({
      open: ({ draft }) => draft.adminOpen,
      draft: ({ draft }) => draft.draft,
    }),

    ...mapGetters({
      isCommissioner: 'draft/isCommissioner',
      isCurrentlyDrafting: 'draft/isCurrentlyDrafting',
      isDraftComplete: 'draft/isDraftComplete',
    }),

    showAvailable() {
      return this.draft.status === 'running'
        && (this.isCommissioner || this.isCurrentlyDrafting)
        && !this.isDraftComplete;
    },

    showUndo() {
      return this.draft.status === 'running' && this.draft.currentPick > 1;
    },

    startButtonText() {
      return this.starting ? 'Starting' : 'Start draft';
    },

    availablePlayers() {
      return this.$store.getters['draft/availablePlayers']
        .filter(({ name, position }) => {
          const fitsSearch = this.search === ''
            || name.toLowerCase().indexOf(this.search.toLowerCase()) > -1;
          const fitsPosition = this.positionFilter === 'ALL' || position === this.positionFilter;

          return fitsSearch && fitsPosition;
        });
    },
  },

  methods: {
    close() {
      this.$store.commit('draft/SET_ADMIN_OPEN', false);
    },

    start() {
      this.starting = true;

      this.$store.dispatch('draft/START_DRAFT')
        .then(() => {
          this.starting = false;
        });
    },

    pause() {
      this.$store.dispatch('draft/PAUSE_DRAFT');
    },

    resetDraft() {
      this.$confirm({ title: 'Reset draft', message: 'Are you sure you want to reset this draft?' })
        .then(() => {
          this.$store.dispatch('draft/SETUP_DRAFT');
          this.close();
        })
        .catch(() => {});
    },

    undo() {
      this.$confirm({ title: 'Undo pick', message: 'Are you sure you want to undo the last pick?' })
        .then(() => {
          this.$store.dispatch('draft/UNDO_PICK');
          this.close();
        })
        .catch(() => {});
    },

    selectPlayer(player) {
      this.selectedPlayer = player;
    },

    cancel() {
      this.selectedPlayer = null;
    },

    fullScreen() {
      this.$store.commit('draft/SET_FULL_SCREEN', true);
      this.$store.commit('draft/SET_ADMIN_OPEN', false);

      const el = document.documentElement;
      const rfs = el.requestFullscreen
          || el.webkitRequestFullScreen
          || el.mozRequestFullScreen
          || el.msRequestFullscreen;

      rfs.call(el);
    },

    draftPlayer(player) {
      this.close();

      setTimeout(() => {
        this.$store.dispatch('draft/DRAFT_PLAYER', player.id);
        this.search = '';
        this.cancel();
      }, 500);
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  $width: px-to-rem(360px);

  nav {
    @include transition(transform);

    min-height: 0;
    position: fixed;
    box-shadow: $drop-shadow-1;
    background-color: $white-color;
    width: $width;
    transform: translateX(0);
    top: 0;
    bottom: 0;
    left: -$width;
    display: flex;
    flex-direction: column;
    z-index: 2;

    &.open {
      transform: translateX(100%);
    }
  }

  header {
    padding: $gp;
    padding-bottom: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      margin-bottom: 0;
    }

    .back {
      display: block;
      color: $black-color;
      font-size: px-to-rem(24px);
    }

    .fa-times {
      font-size: px-to-rem(24px);
    }
  }

  .actions {
    padding: $gp;
  }

  .main-actions {
    display: flex;
    align-items: center;
    font-size: px-to-rem(30px);

    .button-group {
      flex: 1;
    }

    > *:not(:last-child) {
      margin-right: $gp;
    }
  }

  .start-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: $gp;
    flex: 1;
    margin-bottom: $gp * 4;
  }

  .search-container {
    padding: 0 $gp;
    flex: 1;

    input {
      margin-bottom: 0;
    }
  }

  .available {
    display: flex;
    flex-direction: column;
    flex: 1;

    .players-container {
      flex: 1;
      overflow-y: auto;
    }

    .player {
      cursor: pointer;

      &.selected {
        background-color: $gray-color-light;
        font-weight: $font-weight-semibold;
      }

      &:not(.selected) {
        &:hover {
          background-color: $gray-color-lighter;
        }

        &:active {
          background-color: $gray-color-light;
        }
      }
    }

    .player-info {
      padding: $gp $gp $gp $gp * 1.5;
      display: flex;
      align-items: center;
    }

    .player-actions {
      display: flex;
      justify-content: space-between;
      padding: $gp;
    }

    .number {
      color: $primary-color;
      font-weight: $font-weight-bold;
      width: px-to-rem(40px);
    }

    .position {
      width: px-to-rem(30px);
    }

    .name {
      flex: 1;
    }

    .team {
      width: px-to-rem(50px);
    }

    .position,
    .team {
      color: darken($gray-color, 20%);
    }
  }
</style>
